import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Modal, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { acceptInvitationApi } from '../../api/invitations';
import '../css/login.css';

export const INVITATION_STATUS = {
  VERIFYING: 'verifying',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

const DEFAULT_ERROR =
  'This invitation link is no longer valid. Ask your administrator to send a new one.';

// Landing page for the invitation link mailed to a user
// (/invitations/accept/:token). It verifies the token on arrival and reports
// the outcome; either way the only way on is the login screen.
const InvitationAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(INVITATION_STATUS.VERIFYING);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR);
  // React 18 StrictMode mounts effects twice in development; accepting a token
  // is not idempotent, so only ever send it once.
  const verifiedRef = useRef(false);

  useEffect(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    if (!token) {
      setErrorMessage(DEFAULT_ERROR);
      setStatus(INVITATION_STATUS.REJECTED);
      return;
    }

    (async () => {
      try {
        await acceptInvitationApi(token);
        setStatus(INVITATION_STATUS.ACCEPTED);
      } catch (e) {
        const data = e?.response?.data;
        const message = typeof data === 'string' ? data : data?.message || data?.error;
        setErrorMessage(message || DEFAULT_ERROR);
        setStatus(INVITATION_STATUS.REJECTED);
      }
    })();
  }, [token]);

  const goToLogin = () => navigate('/login');

  const accepted = status === INVITATION_STATUS.ACCEPTED;

  return (
    <div className="Login">
      <Container>
        <Row>
          <Col>
            <Container>
              <Row className="flex-column mt-2">
                <Col className="Login-info-logo">
                  <img src="/bento-logo.svg" alt="Bento Logo" />
                  <span>Bento</span>
                </Col>
                <Col className="Login-info-welcome">Welcome to</Col>
                <h2 className="Login-info-title">Bento</h2>
                <h3 className="Login-info-subtitle">The Benchmarking Tool</h3>
              </Row>
              {status === INVITATION_STATUS.VERIFYING && (
                <Row className="mt-4">
                  <Col className="d-flex flex-column align-items-center">
                    <Spinner animation="border" role="status" />
                    <p className="mt-3">Verifying your invitation…</p>
                  </Col>
                </Row>
              )}
            </Container>
          </Col>
        </Row>
      </Container>

      <Modal show={status !== INVITATION_STATUS.VERIFYING} backdrop="static" centered>
        <Modal.Dialog>
          <Modal.Header>
            <Modal.Title>{accepted ? 'Invitation Accepted' : 'Invitation Not Valid'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {accepted
                ? 'Your invitation has been accepted. Sign in to start using Bento.'
                : errorMessage}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" className="btn btn-custom dark" onClick={goToLogin}>
              Go to Login
            </Button>
          </Modal.Footer>
        </Modal.Dialog>
      </Modal>
    </div>
  );
};

export default InvitationAccept;
